import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { MainLayout } from '../components/Layout';

// Import shared dashboard components
import {
    DashboardHeader,
    DashboardTabs,
    DEFAULT_DASHBOARD_TABS,
} from '../components/dashboard/common';
import { MonitorIcon } from '../Icons/DashboardIcons';
import { Chip } from '@mui/material';

// Import MBF-specific tab components
import { MBFUserStatisticsTab, MBFEngagementTab, MBFProjectsTab, MBFMachineUsageTab, MBFMISDataTab, MBFLiveSimulationTab } from '../components/dashboard/mbf';

// Import API
import MBFDashboardAPIs from '../utilities/api/dashboard/MBFDashboardAPIs';
import SimulationAPIs from '../utilities/api/simulation/SimulationAPIs';

const MBFDashboard = ({ collapsed, setCollapsed }) => {
    const { user } = useSelector((state) => state.user.user);
    const [lastUpdated, setLastUpdated] = useState('');
    const [dateRange, setDateRange] = useState(() => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 60);
        return {
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        };
    });
    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = localStorage.getItem('mbfDashboardActiveTab');
        return savedTab || 'User Statistics';
    });
    const [selectedTL, setSelectedTL] = useState('');
    const [tlOptions, setTLOptions] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [isSimulation, setIsSimulation] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Fetch TL options on mount
    useEffect(() => {
        const fetchTLList = async () => {
            try {
                const response = await MBFDashboardAPIs.getTLList();
                // Map response to expected format { id, name }
                const options = (response || []).map(tl => ({
                    id: tl.id?.toString() || '',
                    name: tl.name || ''
                })).filter(tl => tl.name !== 'MBF Admin');
                setTLOptions(options);
            } catch (error) {
                console.error('Error fetching TL list:', error);
                // Fallback to empty array
                setTLOptions([]);
            }
        };
        fetchTLList();
    }, []);

    useEffect(() => {
        if (isMobile) {
            setCollapsed(true);
        }
    }, [isMobile, setCollapsed]);

    useEffect(() => {
        updateTimestamp();

        const checkSimulation = async () => {
            try {
                const status = await SimulationAPIs.getStatus();
                setIsSimulation(status.is_simulation);
            } catch (error) {
                console.error('Error fetching simulation status:', error);
            }
        };
        checkSimulation();
    }, []);

    const updateTimestamp = () => {
        const now = new Date();
        const options = {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        };
        const formattedDate = now.toLocaleString('en-US', options).replace(',', '');
        setLastUpdated(formattedDate);
    };

    const handleDateRangeChange = (range) => {
        setDateRange(range);
    };

    const handleRefresh = () => {
        updateTimestamp();
        // Increment refresh key to trigger re-fetch in child components
        setRefreshKey(prev => prev + 1);
    };

    // Map active tab to API data_type parameter
    const getDataTypeFromTab = (tab) => {
        switch (tab) {
            case 'User Statistics': return 'user-statistics';
            case 'Engagement': return 'engagement';
            case 'Projects': return 'projects';
            case 'Machine Usage': return 'machine-usage';
            case 'MIS Data': return 'mis-data';
            default: return 'user-statistics';
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const dataType = getDataTypeFromTab(activeTab);
            const blob = await MBFDashboardAPIs.exportCSV({
                dataType,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                tlId: selectedTL || undefined,
            });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Create filename with date range info
            const dateInfo = dateRange.startDate && dateRange.endDate
                ? `${dateRange.startDate}-to-${dateRange.endDate}`
                : new Date().toISOString().split('T')[0];
            const tlInfo = selectedTL ? `-tl${selectedTL}` : '';
            link.download = `mbf-dashboard-${dataType}${tlInfo}-${dateInfo}.csv`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert('Failed to download CSV. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const handleTLChange = (tlId) => {
        setSelectedTL(tlId);
    };

    const handleTabChange = (tabName) => {
        setActiveTab(tabName);
        localStorage.setItem('mbfDashboardActiveTab', tabName);
        
        // Auto-collapse sidebar for Live Feed
        if (setCollapsed) {
            if (tabName === 'Live Feed') {
                setCollapsed(true);
            } else {
                setCollapsed(false);
            }
        }
    };

    /**
     * Render the active tab content
     */
    const renderTabContent = () => {
        const commonProps = {
            dateRange,
            selectedTL,
            key: refreshKey, // Force re-render on refresh
        };

        switch (activeTab) {
            case 'User Statistics':
                return <MBFUserStatisticsTab {...commonProps} />;

            case 'Engagement':
                return <MBFEngagementTab {...commonProps} />;

            case 'Projects':
                return <MBFProjectsTab {...commonProps} />;

            case 'Machine Usage':
                return <MBFMachineUsageTab {...commonProps} />;

            case 'MIS Data':
                return <MBFMISDataTab {...commonProps} />;

            case 'Live Feed':
                return <MBFLiveSimulationTab selectedTL={selectedTL} />;

            default:
                return <MBFUserStatisticsTab {...commonProps} />;
        }
    };

    return (
        <MainLayout collapsed={collapsed} setCollapsed={setCollapsed}>
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    bgcolor: '#ffffff',
                    p: { xs: 1, sm: 1.5, md: 2 },
                }}
            >
                <Box sx={{ px: { xs: 0, sm: 0.5, md: 1 } }}>
                    {/* Dashboard Header with TL Selector and Date Range Picker */}
                    <DashboardHeader
                        title="Dashboard"
                        lastUpdated={lastUpdated}
                        simulationMode={isSimulation}
                        onRefresh={handleRefresh}
                        onDownload={handleDownload}
                        downloading={downloading}
                        selectorLabel="Select TL"
                        selectorOptions={tlOptions}
                        selectedOption={selectedTL}
                        onOptionChange={handleTLChange}
                        showSelector={true}
                        useRangePicker={true}
                        dateRange={dateRange}
                        onDateRangeChange={handleDateRangeChange}
                        showLiveFeedButton={true}
                        onLiveFeedClick={() => handleTabChange(activeTab === 'Live Feed' ? 'User Statistics' : 'Live Feed')}
                        isLiveFeedActive={activeTab === 'Live Feed'}
                    />

                    {/* Dashboard Tabs */}
                    {activeTab !== 'Live Feed' && (
                        <DashboardTabs
                            tabs={DEFAULT_DASHBOARD_TABS.filter(tab => tab !== 'Live Feed')}
                            activeTab={activeTab === 'Live Feed' ? 'User Statistics' : activeTab}
                            onTabChange={handleTabChange}
                        />
                    )}

                    {/* Tab Content - MBF Specific Charts */}
                    <Box
                        sx={{
                            bgcolor: activeTab === 'Live Feed' ? 'transparent' : '#ffffff',
                            borderRadius: { xs: '8px', md: '12px' },
                            padding: activeTab === 'Live Feed' ? 0 : { xs: '12px', sm: '16px', md: '24px' },
                            boxShadow: activeTab === 'Live Feed' ? 'none' : '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                            minHeight: '400px',
                        }}
                    >
                        {renderTabContent()}
                    </Box>
                </Box>
            </Box>
        </MainLayout>
    );
};

export default MBFDashboard;